import { useEffect, useRef, useState } from "react";
import { Loader2, Upload, FileText, X } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { formatPayMonth } from "@/components/SalaryFormSheet";

const MAX_BYTES = 20 * 1024 * 1024;
const ALLOWED = [
  "application/pdf",
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/heic",
];

const numberOrEmpty = (v) => {
  if (v === "" || v === null || v === undefined) return "";
  const n = Number(v);
  return Number.isFinite(n) ? String(n) : "";
};

const formatBytes = (bytes) => {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export function AccountingFormDialog({
  open,
  onOpenChange,
  salary,
  employee,
  onSaved,
}) {
  const [form, setForm] = useState({
    net_salary: "",
    net_bonus: "",
    net_commission: "",
    szocho: "",
  });
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const fileRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setForm({
      net_salary: numberOrEmpty(salary?.net_salary),
      net_bonus: numberOrEmpty(salary?.net_bonus),
      net_commission: numberOrEmpty(salary?.net_commission),
      szocho: numberOrEmpty(salary?.szocho),
    });
    setFile(null);
    setErr("");
  }, [open, salary]);

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!ALLOWED.includes(f.type)) {
      setErr("Nem támogatott fájltípus. PDF, JPG, PNG vagy WebP fájlt tölts fel.");
      e.target.value = "";
      return;
    }
    if (f.size > MAX_BYTES) {
      setErr(`A fájl mérete meghaladja a maximumot (${MAX_BYTES / (1024 * 1024)} MB).`);
      e.target.value = "";
      return;
    }
    setErr("");
    setFile(f);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!salary?._id) return;
    if (form.net_salary === "" || !Number.isFinite(Number(form.net_salary))) {
      setErr("A nettó bér megadása kötelező.");
      return;
    }
    if (!file) {
      setErr("A bérjegyzék feltöltése kötelező.");
      return;
    }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("net_salary", String(Number(form.net_salary)));
      if (form.net_bonus !== "") fd.append("net_bonus", String(Number(form.net_bonus)));
      if (form.net_commission !== "") fd.append("net_commission", String(Number(form.net_commission)));
      if (form.szocho !== "") fd.append("szocho", String(Number(form.szocho)));
      fd.append("file", file);
      const { data } = await api.post(`/salaries/${salary._id}/accounting`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Könyvelés rögzítve. A bér kifizetésre vár.");
      onOpenChange(false);
      if (onSaved) onSaved(data);
    } catch (error) {
      setErr(formatApiError(error?.response?.data?.detail) || "Mentés sikertelen.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="accounting-dialog"
        className="sm:max-w-lg"
      >
        <DialogHeader>
          <DialogTitle>Könyvelés</DialogTitle>
          <DialogDescription>
            Add meg a nettó értékeket és tölts fel bérjegyzéket.
            {salary && (
              <>
                {" "}
                <span className="font-medium text-foreground">
                  {formatPayMonth(salary.pay_month)}
                </span>
                {employee && (
                  <>
                    {" · "}
                    {`${employee.last_name || ""} ${employee.first_name || ""}`.trim()}
                  </>
                )}
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} className="space-y-5">
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Nettó bér (Ft) *" testId="acc-net-salary">
              <Input
                type="number"
                inputMode="numeric"
                step="1"
                min="0"
                value={form.net_salary}
                onChange={(e) =>
                  setForm((f) => ({ ...f, net_salary: e.target.value }))
                }
                placeholder="0"
                className="font-mono"
              />
            </Field>
            <Field label="Nettó jutalék (Ft)" testId="acc-net-commission">
              <Input
                type="number"
                inputMode="numeric"
                step="1"
                min="0"
                value={form.net_commission}
                onChange={(e) =>
                  setForm((f) => ({ ...f, net_commission: e.target.value }))
                }
                placeholder="0"
                className="font-mono"
              />
            </Field>
            <Field label="Nettó bónusz (Ft)" testId="acc-net-bonus">
              <Input
                type="number"
                inputMode="numeric"
                step="1"
                min="0"
                value={form.net_bonus}
                onChange={(e) =>
                  setForm((f) => ({ ...f, net_bonus: e.target.value }))
                }
                placeholder="0"
                className="font-mono"
              />
            </Field>
            <Field label="Szocho (Ft)" testId="acc-szocho">
              <Input
                type="number"
                inputMode="numeric"
                step="1"
                min="0"
                value={form.szocho}
                onChange={(e) =>
                  setForm((f) => ({ ...f, szocho: e.target.value }))
                }
                placeholder="0"
                className="font-mono"
              />
            </Field>
          </div>

          <Field label="Bérjegyzék (PDF/JPG/PNG/WebP) *" testId="acc-file">
            <input
              ref={fileRef}
              type="file"
              accept={ALLOWED.join(",")}
              className="hidden"
              onChange={onPickFile}
              data-testid="acc-file-input"
            />
            {file ? (
              <div
                data-testid="acc-file-selected"
                className="flex items-center gap-3 rounded-lg border border-border bg-muted/30 px-3 py-2.5"
              >
                <FileText className="h-4 w-4 shrink-0 text-muted-foreground" strokeWidth={1.75} />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{file.name}</p>
                  <p className="text-[11px] text-muted-foreground">
                    {formatBytes(file.size)}
                  </p>
                </div>
                <button
                  type="button"
                  data-testid="acc-file-clear"
                  onClick={() => {
                    setFile(null);
                    if (fileRef.current) fileRef.current.value = "";
                  }}
                  className="grid h-7 w-7 place-items-center rounded-md text-muted-foreground hover:bg-foreground/[0.06] hover:text-foreground"
                  aria-label="Fájl eltávolítása"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ) : (
              <button
                type="button"
                data-testid="acc-file-button"
                onClick={() => fileRef.current?.click()}
                className={cn(
                  "flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-border bg-muted/20 px-3 py-3 text-sm text-muted-foreground transition-colors",
                  "hover:border-foreground/30 hover:bg-muted/40 hover:text-foreground"
                )}
              >
                <Upload className="h-4 w-4" strokeWidth={1.75} />
                Bérjegyzék kiválasztása
              </button>
            )}
          </Field>

          {err && (
            <div
              data-testid="acc-error"
              className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive"
            >
              {err}
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              data-testid="acc-cancel"
            >
              Mégse
            </Button>
            <Button type="submit" disabled={saving} data-testid="acc-submit">
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Mentés…
                </>
              ) : (
                "Könyvelés mentése"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children, testId }) {
  return (
    <label className="block space-y-1.5" data-testid={testId}>
      <span className="block text-xs font-medium text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}
